// libs
import React, { memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

// constants
import { DEFAULT_CURRENCY_CODE } from 'shared/constants';
import { NOINDEX_NOFOLLOW, TEXT } from 'shared/layouts/DetailsPage/constants';
import { extractStoreKey } from 'shared/helpers/storeUrl';

// hooks
import {
  useIsSelectedSKUOOS,
  useSelectedSKUDiscount,
  useSelectedSKUDiscountPrice,
  useSelectedSKUMetaData,
  useSelectedSKUPrice,
  useSelectedSKUSubTitle,
  useSelectedSKUTitle,
  useSelectedSKUFaq,
} from 'shared/store/details/hooks/selectors';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// selectors
import { getStorePriority } from 'shared/store/details/selectors';
import { getStoreId } from 'shared/store/app/selectors';

// helpers
import { getJSONLD, getBreadcrumbListSchema } from './helpers';
import { getFaqPageSchema, safeJSONStringify } from 'shared/helpers/meta';

// types
import { BreadCrumbs } from 'shared/types/breadCrumbs';

// context
import { useDomainConfig } from 'shared/domainConfig/context/context';

interface IMetaProps {
  actionUrl: string;
  SKU: string;
  review: Review.IReviewSummary;
  breadCrumbs: BreadCrumbs;
}

function MetaInfo({ actionUrl, SKU, review, breadCrumbs }: IMetaProps) {
  const ios_action_url = TEXT.IOS_ACTION_URL_PREFIX + actionUrl;

  const metaInfo = useSelectedSKUMetaData();
  const title = useSelectedSKUTitle();
  const subTitle = useSelectedSKUSubTitle();
  const hasDiscount = Number(useSelectedSKUDiscount()) > 0;
  const price = useSelectedSKUPrice();
  const discountPrice = useSelectedSKUDiscountPrice();
  const isOutOfStock = useIsSelectedSKUOOS();
  const faq = useSelectedSKUFaq();
  const isAeoFaqEnabled = Boolean(useRemoteConfig('isAeoFaqEnabled'));

  const storeId = useSelector(getStoreId);
  const storePriority = useSelector(getStorePriority);

  const { FAVICON } = useDomainConfig();

  // Suppress indexing when the current store is not the highest-priority store for this product.
  // Fail-safe: if store_priority is absent from the API response (e.g. Footlocker env),
  // storePriority is [] and isNoIndexMeta stays false — preserving previous behaviour.
  const isNoIndexMeta = storePriority.length > 0 && extractStoreKey(storePriority[0]) !== storeId;

  const JSONLDData = getJSONLD({
    metaInfo,
    title,
    subTitle,
    SKU,
    isOutOfStock,
    price,
    hasDiscount,
    discountPrice,
    review,
  });

  const breadcrumbSchema = getBreadcrumbListSchema(breadCrumbs);
  const faqSchema = isAeoFaqEnabled ? getFaqPageSchema(faq) : null;

  return (
    <Helmet>
      <title>{metaInfo.title}</title>

      <meta name="description" content={metaInfo.description} />
      <meta name="keywords" content={metaInfo.keywords} />

      <meta property="al:ios:url" content={ios_action_url} />
      <meta property="al:ios:app_store_id" content={TEXT.APP_STORE_ID} />
      <meta property="al:ios:app_name" content={TEXT.GLOBAL_BRAND_NAME} />

      <meta property="og:type" content="og:product" />
      <meta property="og:title" content={metaInfo?.title} />
      <meta property="og:image" content={metaInfo?.imgUrl} />
      <meta property="og:image:alt" content={metaInfo?.title} />
      <meta property="og:description" content={metaInfo?.description} />

      <meta property="og:url" content={metaInfo?.productUrl} />

      <meta property="og:site_name" content={TEXT.GLOBAL_BRAND_NAME} />

      <meta name="twitter:title" content={metaInfo?.title} />
      <meta name="twitter:description" content={metaInfo?.description} />
      <meta name="twitter:image" content={metaInfo?.imgUrl} />
      <meta name="twitter:card" content="summary_large_image" />

      <meta property="product:price:amount" content={metaInfo?.amount} />
      <meta property="product:price:currency" content={DEFAULT_CURRENCY_CODE} />

      <link rel="canonical" href={metaInfo?.productUrl} />
      <link rel="icon" type="image/x-icon" href={FAVICON} />
      <link rel="shortcut icon" type="image/x-icon" href={FAVICON} />
      {isNoIndexMeta && <meta name="robots" content={NOINDEX_NOFOLLOW} />}
      {!isEmpty(metaInfo?.intl_url) &&
        Object.keys(metaInfo.intl_url).map((val) => {
          return <link rel="alternate" hrefLang={val} href={metaInfo?.intl_url[val]} key={val} />;
        })}

      <script type="application/ld+json">{JSONLDData}</script>

      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}

      {faqSchema && (
        <script type="application/ld+json">{safeJSONStringify(faqSchema)}</script>
      )}
    </Helmet>
  );
}

export default memo(MetaInfo);
